import { useCallback, useEffect, useRef, useState, type JSX, type RefObject } from 'react';
import { pathBetween, sceneryVariant, stepFrom, type Direction, type Position, type Terrain, type TileGrid } from '@yuan-math/world';

/**
 * The parts every map shares: the ground, the walk and the words for both.
 *
 * There are three scales of tile map on this site — the continent in
 * `Overworld.tsx`, a kingdom's village and a region's own inside, both in
 * `TownMap.tsx` — and the difference between them is entirely what stands *on*
 * the tiles. A meadow is a meadow at every scale; a step is a step; a route is
 * the same breadth-first search in `packages/world`. This file is what stops
 * that being written three times, because the second copy is the one that
 * quietly stops honouring `prefers-reduced-motion`.
 *
 * Everything here is presentation. The walking itself — `stepFrom`,
 * `pathBetween` — is pure and lives below `apps/web`, and nothing in this file
 * knows what a kingdom, a region or a seal is.
 */

/** How long a single step takes. Stilled entirely under reduced motion. */
export const STEP_MS = 130;

/** What the live region calls the ground you are standing on, at continent scale. */
export const TERRAIN_WORD: Record<Terrain, string> = {
  grass: 'meadow',
  road: 'road',
  bridge: 'bridge',
  forest: 'forest',
  sand: 'shore',
  water: 'sea',
  mountain: 'mountains',
  floor: 'floor',
  wall: 'wall',
  crop: 'crop',
  snow: 'snow',
  dune: 'dunes',
};

/**
 * The same twelve, as a village and the rooms in it see them.
 *
 * The same tile is a different word at a different scale — a highway is a lane,
 * the sea is the mill pond — and getting that wrong is how the sentence under
 * the map stops being written by a person. This one serves the towns and the
 * region maps both, which is why it has words for a floor and a wall.
 */
export const VILLAGE_WORD: Record<Terrain, string> = {
  grass: 'meadow',
  road: 'path',
  bridge: 'bridge',
  forest: 'trees',
  sand: 'village green',
  water: 'water',
  mountain: 'hillside',
  floor: 'floorboards',
  wall: 'wall',
  crop: 'crop rows',
  /* No village on this platform is in the snow or the sand sea, but the table
     is total and a word is cheaper than an exception. */
  snow: 'snow',
  dune: 'sand',
};

export const KEYS: Record<string, Direction> = {
  ArrowUp: 'north',
  ArrowDown: 'south',
  ArrowLeft: 'west',
  ArrowRight: 'east',
  w: 'north',
  a: 'west',
  s: 'south',
  d: 'east',
  W: 'north',
  A: 'west',
  S: 'south',
  D: 'east',
};

/* -------------------------------------------------------------------------- */
/* Scenery                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * What sits on top of a tile.
 *
 * One `<path>` each, three variants apiece, chosen by the tile's coordinates.
 * Everything is `currentColor` or a theme token so both palettes and both
 * themes come for free — a pixel tileset would have needed two of every file.
 * Which variant a tile gets is derived from where the tile *is*, never from
 * `Math.random`: a forest of identical trees looks like wallpaper, and a forest
 * of random ones reshuffles itself under the student on every render.
 */
export function Scenery({ terrain, position }: { terrain: Terrain; position: Position }): JSX.Element | null {
  const variant = sceneryVariant(position, 3);

  switch (terrain) {
    case 'forest':
      /* A tree with a lit side and a shaded one. The light falls from the
         north-west on every drawing on this map, which is what stops a hillside
         and a wood looking like two different weathers. */
      return (
        <svg className="ow-tile__art" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          <rect x="9.2" y="13" width="1.6" height="5" rx="0.6" className="ow-art__trunk" />
          <path d="M10 2.5 L14.8 11 H5.2 Z" className="ow-art__canopy" />
          <path d="M10 2.5 L10 11 H5.2 Z" className="ow-art__canopy-lit" />
          {variant > 0 ? (
            <>
              <path d="M10 6.5 L15.6 15 H4.4 Z" className="ow-art__canopy" />
              <path d="M10 6.5 L10 15 H4.4 Z" className="ow-art__canopy-lit" />
            </>
          ) : null}
          {variant === 2 ? (
            <>
              <rect x="3.4" y="13" width="1.2" height="4" rx="0.5" className="ow-art__trunk" />
              <path d="M4 8 L6.8 14 H1.2 Z" className="ow-art__canopy" />
            </>
          ) : null}
        </svg>
      );
    case 'mountain':
      /* Two faces to a peak, the north-west one lit — without it a range is a
         row of grey triangles and reads as a fence rather than as ground. */
      return (
        <svg className="ow-tile__art" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          {variant > 0 ? (
            <>
              <path d="M9 18 L14 8 L19 18 Z" className="ow-art__rock" />
              <path d="M9 18 L14 8 L14 18 Z" className="ow-art__rock-lit" />
            </>
          ) : null}
          <path d="M0.5 18 L7.5 3.5 L14.5 18 Z" className="ow-art__rock" />
          <path d="M0.5 18 L7.5 3.5 L7.5 18 Z" className="ow-art__rock-lit" />
          <path d="M5.2 9 L7.5 3.5 L9.8 9 L7.5 7.6 Z" className="ow-art__snow" />
        </svg>
      );
    case 'water':
      return (
        <svg className="ow-tile__art" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          <path
            d={variant === 1 ? 'M2 12 q3 -2 6 0 t6 0' : 'M4 8 q3 -2 6 0 t6 0'}
            className="ow-art__wave"
            fill="none"
          />
        </svg>
      );
    case 'grass':
      return variant === 0 ? null : (
        <svg className="ow-tile__art" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          <path
            d={
              variant === 1
                ? 'M5 16 q1.4 -4.5 2.6 -1.2 M11 17 q1.2 -5 2.4 -2'
                : 'M8 17 q1.3 -5 2.5 -2 M14 15 q0.9 -3.4 1.8 -0.9'
            }
            className="ow-art__tuft"
            fill="none"
          />
        </svg>
      );
    case 'sand':
      return variant === 2 ? (
        <svg className="ow-tile__art" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          <circle cx="7" cy="12" r="1.3" className="ow-art__pebble" />
          <circle cx="12" cy="9" r="0.9" className="ow-art__pebble" />
        </svg>
      ) : null;
    case 'dune':
      /* A dune crest and its wind-shadow, from the tile's own coordinates.
         Sparser than snow: an erg reads as emptiness, and a busy desert is a
         beach. */
      return variant === 1 ? (
        <svg className="ow-tile__art" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          <path d="M1 13 Q6 8 11 12 T19 10" className="ow-art__crest" fill="none" />
        </svg>
      ) : variant === 2 ? (
        <svg className="ow-tile__art" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          <path d="M3 15 Q9 11 17 14" className="ow-art__crest" fill="none" />
        </svg>
      ) : null;
    case 'snow':
      /* falls through to the drift below */
      /* Drift and crust, derived from the tile's own coordinates like every
         other tuft on this map — `packages/world` sits below `apps/web` and a
         `Math.random()` snowfield would reshuffle itself under the walker. */
      return variant === 0 ? null : (
        <svg className="ow-tile__art" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          {variant === 1 ? (
            <path d="M2 14 Q7 11 11 14 T19 13" className="ow-art__drift" fill="none" />
          ) : (
            <>
              <circle cx="6" cy="8" r="1" className="ow-art__flake" />
              <circle cx="13" cy="12" r="1.2" className="ow-art__flake" />
              <circle cx="9" cy="15" r="0.8" className="ow-art__flake" />
            </>
          )}
        </svg>
      );
    case 'crop':
      return (
        <svg className="ow-tile__art" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          <path
            d={
              variant === 0
                ? 'M5 17 V7 M10 18 V6 M15 17 V8'
                : variant === 1
                  ? 'M4 18 V8 M9 17 V6 M14 18 V7'
                  : 'M6 17 V6 M11 18 V8 M16 17 V7'
            }
            className="ow-art__stalk"
            fill="none"
          />
        </svg>
      );
    /*
     * A floor is boards, and a wall is courses of stone. Both are drawn as a
     * couple of lines rather than as a texture: at 38 pixels a pattern is
     * noise, and what the eye needs from these two is only "indoors" and
     * "not through here".
     */
    case 'floor':
      return (
        <svg className="ow-tile__art" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          <path d={variant === 1 ? 'M0 7 H20 M0 14 H20' : 'M0 10 H20'} className="ow-art__board" fill="none" />
        </svg>
      );
    case 'wall':
      return (
        <svg className="ow-tile__art" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          <path
            d={variant === 1 ? 'M0 7 H20 M0 14 H20 M10 0 V7 M6 14 V20' : 'M0 7 H20 M0 14 H20 M13 0 V7 M8 7 V14'}
            className="ow-art__course"
            fill="none"
          />
        </svg>
      );
    default:
      return null;
  }
}

/* -------------------------------------------------------------------------- */
/* Walking                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Whether this student has asked for less movement.
 *
 * Every other animation on the site is a CSS class and is stilled by the one
 * rule in `theme.css`. A walk is not: the steps are state changes over time, so
 * this is the one place the preference has to be *read*. Guarded, because a
 * test environment may not implement `matchMedia` — and the honest default when
 * nobody has said is to animate, exactly as the CSS does.
 */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(query.matches);
    const listen = (event: MediaQueryListEvent): void => setReduced(event.matches);
    query.addEventListener?.('change', listen);
    return () => query.removeEventListener?.('change', listen);
  }, []);

  return reduced;
}

/**
 * Where the ground sits under the frame.
 *
 * The camera used to be one line — put the canvas at the middle of the frame
 * and slide it by however far the student has walked — which is right for a
 * continent and wrong for everything smaller than the window. A cottage is
 * seventeen tiles across, the frame around it is exactly seventeen tiles wide,
 * and centring on a student standing near its door showed them half a cottage
 * and half a field of nothing.
 *
 * So the slide is clamped to the map's own edges: follow the walk while there
 * is map left to reveal, stop at the coastline, and when an axis fits in the
 * frame entirely, centre it and never move it again. The whole meadow is then
 * simply *there*, which is what a map of a meadow should be.
 *
 * Measured rather than assumed, because the tile size is a token that changes
 * with `data-density` and with the width of the window. Both refs are wanted:
 * the frame is what the map has to fit inside, and the canvas is how wide the
 * map turned out to be at the current tile size.
 */
export function useCamera(
  map: TileGrid,
  position: Position,
): { frameRef: RefObject<HTMLDivElement | null>; canvasRef: RefObject<HTMLDivElement | null>; transform: string } {
  const frameRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ frameW: 0, frameH: 0, tile: 0 });

  useEffect(() => {
    const frame = frameRef.current;
    const canvas = canvasRef.current;
    if (!frame || !canvas) return undefined;

    const measure = (): void =>
      setBox({
        frameW: frame.clientWidth,
        frameH: frame.clientHeight,
        tile: map.width > 0 ? canvas.offsetWidth / map.width : 0,
      });

    measure();
    // Guarded: jsdom has no ResizeObserver, and a test that never resizes
    // anything needs one measurement rather than a subscription.
    if (typeof ResizeObserver !== 'function') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(frame);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [map.width]);

  /*
   * One axis, in pixels. `extent` is how long the map is; `frame` is how much
   * of it can be seen. The canvas is anchored at the middle of the frame, so
   * the offset that keeps the frame's leading edge covered is -frame/2, and the
   * one that keeps its trailing edge covered is frame - extent - frame/2.
   */
  const axis = (index: number, frame: number, extent: number): number => {
    if (extent <= frame || box.tile === 0) return -extent / 2;
    const follow = -(index + 0.5) * box.tile;
    return Math.min(-frame / 2, Math.max(frame / 2 - extent, follow));
  };

  const x = axis(position.x, box.frameW, map.width * box.tile);
  const y = axis(position.y, box.frameH, map.height * box.tile);

  return { frameRef, canvasRef, transform: `translate3d(${x}px, ${y}px, 0)` };
}

export type Walk = {
  position: Position;
  /** Walk the shortest route to a tile. Stable, so tiles need not be redrawn. */
  walkTo: (target: Position) => void;
  /** One step, or exactly where you were if the terrain refuses it. */
  move: (direction: Direction) => void;
};

/**
 * Where the student is standing, and the two ways of changing it.
 *
 * **Session state, deliberately.** Where somebody is standing is not a fact
 * about their mathematics, so it is not on the profile: the walk lives as long
 * as the page does, and a reload puts them back at the tile the page chose.
 * That is one less field for `mergeProfiles` to have an opinion about.
 *
 * `walkTo` is stable across renders even though the route it computes depends
 * on where the student currently is. Every tile on a map carries a click
 * handler, and a `walkTo` that changed with each step would redraw a thousand
 * of them per step — which feels like a page load rather than like walking.
 *
 * `detour` is handed straight to `pathBetween`, and the callers pass "any tile
 * with a signpost on it". Arriving somewhere takes you there, so a tapped route
 * that ran over a gate would carry a student into a kingdom they meant to walk
 * past — a step taken *onto* a signpost with the arrow keys is deliberate, and
 * a route drawn over one is not.
 */
export function useWalk(grid: TileGrid, initial: Position, detour?: (position: Position) => boolean): Walk {
  const [position, setPosition] = useState<Position>(initial);
  const [route, setRoute] = useState<Position[]>([]);
  const stillness = useReducedMotion();

  /*
   * The walk: one tile per tick, and the route is the only thing being held.
   * Under `prefers-reduced-motion` there is no walk at all — the student simply
   * arrives, which is the same journey without the movement.
   */
  useEffect(() => {
    if (route.length === 0) return undefined;
    if (stillness) {
      setPosition(route[route.length - 1] as Position);
      setRoute([]);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setPosition(route[0] as Position);
      setRoute((rest) => rest.slice(1));
    }, STEP_MS);
    return () => window.clearTimeout(timer);
  }, [route, stillness]);

  const walkTo = useCallback(
    (target: Position) => {
      const path = pathBetween(grid, position, target, detour);
      if (path.length > 0) setRoute(path);
    },
    [detour, grid, position],
  );

  // The indirection that lets a tile stay still while the route it would walk
  // moves under it.
  const latest = useRef(walkTo);
  latest.current = walkTo;
  const stableWalkTo = useCallback((target: Position) => latest.current(target), []);

  const move = useCallback(
    (direction: Direction) => {
      setRoute([]);
      setPosition((from) => stepFrom(grid, from, direction));
    },
    [grid],
  );

  return { position, walkTo: stableWalkTo, move };
}
